<script>
  import { ConfirmModal, PolicyModal } from "../../components/index"
  import { faPlus, faTrashAlt, faPen } from "@fortawesome/pro-regular-svg-icons"
  import { faSort, faSortDown, faSortUp } from "@fortawesome/pro-solid-svg-icons"
  import { PoliciesRepository, AppsRepository } from "../../repositories/index"
  import { Button, Flex, FlexItem, Icon, Text, SimpleDataTable, DataCell, TextField, Loader } from "@gl/design-system"
  import { onMount } from "svelte"
  import { _, user } from "../../stores/index"
  import { navigate } from "svelte-routing"

  export let app
  export let id

  const loading = {}

  let policies = []

  let modal
  let confirmModal
  let modalType
  let appType
  let selectedPolicy

  let search = {
    name: "",
  }
  const order = {
    asc: true,
  }
  let searched

  async function fetchPolicies() {
    try {
      loading.policies = true
      const result = await PoliciesRepository.getPolicies(id)

      policies = result.list
    } finally {
      loading.policies = false
    }
  }

  async function handleDelete() {
    try {
      loading.delete = true
      if (app.data.policyDefinition === "external") {
        function mapExternalPolicies(policy) {
          const obj = {
            obj: policy.obj,
            act: policy.act,
          }

          if (policy.description) {
            obj.description = policy.description
          }

          if (policy.implicit) {
            obj.implicit = policy.implicit.split(",")
          }

          return obj
        }

        const filteredPolicies = policies.filter((p) => p.id !== selectedPolicy.id).map((p) => mapExternalPolicies(p))
        await PoliciesRepository.updateAppPolicies(id, filteredPolicies)
      } else {
        await PoliciesRepository.deletePolicy(id, selectedPolicy.id)
      }
    } finally {
      fetchPolicies()
      loading.delete = false
    }
  }

  function sort(field = null, toggleAsc = true) {
    order.asc = !order.asc
    order.by = field

    const cmpA = order.asc ? -1 : 1
    const cmpB = order.asc ? 1 : -1
    searched = searched.sort((a, b) => {
      const A = typeof a[order.by] === "string" ? a[order.by].toUpperCase() : a.meta.createdAt
      const B = typeof b[order.by] === "string" ? b[order.by].toUpperCase() : b.meta.createdAt

      return A === B ? 0 : A < B ? cmpA : cmpB
    })
    order.icon = order.asc ? "ArrowDown" : "ArrowUp"
  }

  const handleSearch = () => {
    const upperCaseSearch = search.name.trim().toUpperCase()
    searched = policies.filter((policy) => policy.name.toUpperCase().includes(upperCaseSearch))
  }

  $: if (!loading.policies && search) handleSearch()

  onMount(() => {
    fetchPolicies()
    if (!$user.data.isMaster) navigate("/forbidden")
  })
</script>

{#if modal}
  <PolicyModal
    bind:active={modal}
    appID={id}
    {policies}
    {modalType}
    {appType}
    {selectedPolicy}
    on:success={fetchPolicies}
    on:close={() => {
      modal = false
      fetchPolicies()
    }}
  />
{/if}

{#if confirmModal}
  <ConfirmModal
    bind:active={confirmModal}
    on:close={() => {
      confirmModal = false
      selectedPolicy = null
    }}
    handleYes={() => handleDelete(selectedPolicy)}
    title={$_("policies.delete")}
  >
    {$_("common.confirm.delete")}
    <b>{selectedPolicy && selectedPolicy.name}</b>?
  </ConfirmModal>
{/if}

<Flex class="mb-5">
  <FlexItem class="mr-4" style="width:300px">
    <TextField variant="Search" bind:value={search.name} />
  </FlexItem>

  <FlexItem pushEnd>
    <Button
      on:click={() => {
        modal = true
        appType = app.data.policyDefinition
        selectedPolicy = null
        modalType = "post"
      }}
    >
      <Icon icon={faPlus} />
      <Text>{$_("policies.add_policy")}</Text>
    </Button>
  </FlexItem>
</Flex>

<div class="table-container">
  <SimpleDataTable
    let:index
    let:row
    data={searched || policies}
    fullWidth
    selectable={false}
    cellsCount="4"
    loading={loading.policies}
    key="id"
    valign="middle"
    variant="separate"
    stickyHead
  >
    <slot name="empty" slot="empty">
      <div class="gl-message" style="text-align: center">{$_("common.no.display")}</div>
    </slot>
    <DataCell
      {index}
      id={`${row.id}-name`}
      header={$_("common.name")}
      headerIcon={order.by === "name" && order.asc ? faSortUp : order.by === "name" && !order.asc ? faSortDown : faSort}
      clickableHeader
      activeHeader
      on:clickHeader={() => sort("name")}
    >
      <!-- svelte-ignore a11y-missing-attribute -->
      <a> <b> {row.name}</b> </a>
      <p class="text-light description" title={row.description}>
        {row.description ?? "-"}
      </p>
    </DataCell>

    <DataCell {index} id={`${row.id}-obj`} header={$_("common.obj")}>
      <p>{row.obj}</p>
    </DataCell>
    <DataCell {index} id={`${row.id}-act`} header={$_("common.act")}>
      <p>{row.act}</p>
    </DataCell>

    <DataCell {index} id={`${row.id}-implicit`} header={$_("common.implicit")}>
      {#if row.implicit}
        <p>{row.implicit}</p>
      {:else}
        -
      {/if}
    </DataCell>

    <DataCell {index} header="Actions">
      <Flex class="justify-content-end">
        <FlexItem>
          <Button
            outlined
            size="tiny"
            on:click={() => {
              modal = true
              selectedPolicy = row
              appType = app.data.policyDefinition
              modalType = "edit"
            }}
            class="mr-2"
          >
            <Icon icon={faPen} />
            <Text>{$_("buttons.edit")}</Text>
          </Button>

          <Button
            outlined
            size="tiny"
            status="error"
            on:click={() => {
              selectedPolicy = row
              confirmModal = true
            }}
          >
            <Icon icon={faTrashAlt} />
            <Text>{$_("buttons.delete")}</Text>
          </Button>
        </FlexItem>
      </Flex>
    </DataCell>
  </SimpleDataTable>
</div>
